import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { ProTip } from '../../components/LessonUI';

const PythonForLoops = () => {
    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">Iteration Mastery</div>
                <h1>For Loops & The Iteration Protocol</h1>
                <p className="hero-lead">
                    Forget <code>for (i=0; i&lt;n; i++)</code>. Python loops are fundamentally different.
                    They are idiomatic, protocol-driven, and designed to work seamlessly with any custom object.
                    Mastering iteration is mastering Python itself.
                </p>
            </div>

            <div className="content-section chapter-intro">
                <p>
                    In C-style languages (C, Java, JavaScript), a <code>for</code> loop is effectively a <code>while</code> loop
                    with counter management boilerplate. In Python, a <code>for</code> loop is a request to an object:
                    "Give me your next item."
                </p>
                <p>
                    This shift from "index-based" to "iterator-based" logic completely changes how we write code.
                    It eliminates off-by-one errors, reduces visual noise, and allows for lazy evaluation of infinite streams.
                    In this deep dive, we will explore not just how to loop, but how the <strong>Iteration Protocol</strong> powers the entire language.
                </p>

                <div className="learning-objectives">
                    <h3>What You'll Learn</h3>
                    <ul>
                        <li><strong>The Protocol:</strong> <code>__iter__</code>, <code>__next__</code>, and <code>StopIteration</code>.</li>
                        <li><strong>Lazy Evaluation:</strong> Why <code>range(10**9)</code> doesn't crash your RAM.</li>
                        <li><strong>Pythonic Patterns:</strong> <code>enumerate()</code>, <code>zip()</code>, and dictionary unpacking.</li>
                        <li><strong>The `else` Block:</strong> The most misunderstood feature of Python loops.</li>
                        <li><strong>Pitfalls:</strong> Modifying a list while iterating over it.</li>
                    </ul>
                </div>
            </div>

            <div className="content-section">
                <h2>The Iteration Protocol: Under the Hood</h2>
                <p>
                    When you write <code>for item in my_list:</code>, Python does a lot of work behind the scenes.
                    It doesn't "know" what a list is; it only cares that the object follows the <strong>Iterator Protocol</strong>.
                </p>

                <h3>1. Iterable vs. Iterator</h3>
                <div className="comparison-table">
                    <table>
                        <thead>
                            <tr>
                                <th>Concept</th>
                                <th>Definition</th>
                                <th>Example</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td><strong>Iterable</strong></td>
                                <td>An object that <em>can</em> be looped over. It has an <code>__iter__()</code> method that returns an Iterator.</td>
                                <td>Lists, Strings, Dicts, Ranges.</td>
                            </tr>
                            <tr>
                                <td><strong>Iterator</strong></td>
                                <td>An object that represents a stream of data. It has a <code>__next__()</code> method that gives the next value.</td>
                                <td>File objects, <code>enumerate()</code> result.</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <h3>2. Simulating a For Loop</h3>
                <p>
                    To truly understand loops, let's manually do what the Python interpreter does.
                </p>
                <CodeBlock language="python" code={`# The High-Level Way
colors = ["red", "green", "blue"]
for color in colors:
    print(color)

# What Python ACTUALLY does (The Low-Level Way)
colors = ["red", "green", "blue"]

# 1. Get the iterator
iterator = iter(colors)  # Calls colors.__iter__()

while True:
    try:
        # 2. Get the next item
        item = next(iterator)  # Calls iterator.__next__()
        print(item)
    except StopIteration:
        # 3. Handle the end of the loop
        break  # The loop finishes gracefully`} />

                <ProTip><div className="info-icon"></div>
                    <div className="info-text">
                        <strong>Why this matters:</strong> This protocol means you can make <em>your own classes</em> loops!
                        Just implement <code>__iter__</code> and <code>__next__</code>, and your custom objects work with
                        <code>for</code> loops, list comprehensions, and unpacking automatically.
                    </div></ProTip>
            </div>

            <div className="content-section">
                <h2>The Power of `range()`</h2>
                <p>
                    In Python 2, <code>range(10)</code> created a list: <code>[0, 1, 2, ..., 9]</code>.
                    In Python 3, <code>range</code> is an immutable sequence type that generates numbers on demand (lazy evaluation).
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>Range Syntax & Memory</span>
                    </div>
                    <CodeBlock language="python" code={`# 1. Basic: 0 to stop-1
for i in range(5):
    print(i)  # 0, 1, 2, 3, 4

# 2. Start & Stop
for i in range(2, 5):
    print(i)  # 2, 3, 4

# 3. Start, Stop, Step
for i in range(0, 10, 2):
    print(i)  # 0, 2, 4, 6, 8

# 4. Negative Step (Reverse)
for i in range(5, 0, -1):
    print(i)  # 5, 4, 3, 2, 1

# Memory Magic
import sys
import { ProTip } from '../../components/LessonUI';
import { ProTip } from '../../components/LessonUI';
# A list of 1 million numbers takes ~8MB
print(sys.getsizeof(list(range(1000000)))) 

# A range object of 1 million numbers takes... 48 bytes!
print(sys.getsizeof(range(1000000)))`} />
                </div>
            </div>

            <div className="content-section">
                <h2>The Cost of Nesting: Big O Analysis</h2>
                <p>
                    A single loop runs <code>n</code> times (Linear Time). But what happens when you put a loop inside a loop?
                </p>
                <p>
                    <strong>Nested loops are multiplicative.</strong> If the outer loop runs 1,000 times and the inner loop runs 1,000 times,
                    the code inside the inner loop runs <strong>1,000,000 times</strong>. This is called Quadratic Time <strong>O(n)</strong>,
                    and it is the #1 killer of performance in Python scripts.
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>The O(n) Trap</span>
                    </div>
                    <CodeBlock language="python" code={`# Task: Find common items in two lists
list_a = range(10000)
list_b = range(5000)

#  The Naive Way (Nested Loops)
common = []
for a in list_a:          # Runs 10,000 times
    for b in list_b:      # Runs 5,000 times PER 'a'
        if a == b:        # Runs 50,000,000 times!
            common.append(a)

# This will freeze your computer for seconds.`} />
                </div>

                <h3>The Optimization: Hash Maps (Dictionaries)</h3>
                <p>
                    We can reduce this from 50 million operations to just 15,000 using a Set or Dictionary.
                    Lookups in sets are <strong>O(1)</strong> (Instant).
                </p>
                <CodeBlock language="python" code={`# The Optimized Way (O(n))
set_b = set(list_b)       # O(n) to create set

common = []
for a in list_a:          # Runs 10,000 times
    if a in set_b:        # O(1) Instant Lookup!
        common.append(a)

# Total operations: ~15,000. 
# This runs instantly.`} />
            </div>

            <div className="content-section">
                <h2>Loop Control Statements</h2>
                <p>
                    Sometimes you need to intervene in the loop's execution. Python provides three keywords for this:
                </p>

                <div className="comparison-table">
                    <table>
                        <thead>
                            <tr>
                                <th>Keyword</th>
                                <th>Action</th>
                                <th>Analogy</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td><code>break</code></td>
                                <td><strong>Terminates</strong> the loop entirely.</td>
                                <td>"Abort mission!"</td>
                            </tr>
                            <tr>
                                <td><code>continue</code></td>
                                <td><strong>Skips</strong> the rest of the current iteration and starts the next one.</td>
                                <td>"Skip this song."</td>
                            </tr>
                            <tr>
                                <td><code>pass</code></td>
                                <td><strong>Does nothing.</strong> It is a placeholder for empty code blocks.</td>
                                <td>"To be decided."</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <h3>The "Placeholder" Pass</h3>
                <p>
                    Unlike `break` or `continue`, `pass` has no effect on logic. It is vital when syntax rules require a statement but you have nothing to do yet.
                </p>
                <CodeBlock language="python" code={`for user in users:
    if user.is_banned:
        # TODO: Implement ban logic later
        pass 
    else:
        email(user)`} />
            </div>

            <div className="content-section">
                <h2>Pythonic Looping: No Indexes Allowed!</h2>
                <p>
                    Coming from C/Java, you might be tempted to write: <code>for i in range(len(items)):</code>.
                    In Python, this is considered an anti-pattern. We have better tools that are cleaner and faster.
                </p>

                <h3>1. enumerate(): The Index Tracker</h3>
                <p>
                    When you need the index, don't fallback to `range(len())`. Use `enumerate()`, which yields pairs of `(index, item)`.
                </p>
                <CodeBlock language="python" code={`names = ["Alice", "Bob", "Charlie"]

#  The Anti-Pattern (Manual Indexing)
for i in range(len(names)):
    print(f"{i + 1}: {names[i]}")

# The Pythonic Way (Enumerate)
# start=1 makes it human-readable (1-based index)
for i, name in enumerate(names, start=1):
    print(f"{i}: {name}")`} />

                <h3>2. zip(): Parallel Iteration</h3>
                <p>
                    Looping over two lists at once? `zip()` stitches them together.
                </p>
                <CodeBlock language="python" code={`names = ["Alice", "Bob"]
scores = [85, 92]

#  The Anti-Pattern
for i in range(len(names)):
    print(f"{names[i]}: {scores[i]}")

# The Pythonic Way
for name, score in zip(names, scores):
    print(f"{name}: {score}")

# Note: zip stops at the shortest list! 
# Use itertools.zip_longest if you need to keep going.`} />

                <h3>3. Dictionary Unpacking</h3>
                <p>
                    Iterating over a dict yields keys by default. Use `.items()` to get everything.
                </p>
                <CodeBlock language="python" code={`scores = {"Alice": 85, "Bob": 92}

# Just keys
for name in scores:
    print(name)

# Keys and Values
for name, score in scores.items():
    print(f"{name} scored {score}")`} />
            </div>

            <div className="content-section">
                <h2>The `else` Block: A Hidden Gem</h2>
                <p>
                    Python loops allow an `else` clause! It's confusingly named, but powerful.
                    Think of it as <strong>"nobreak"</strong>. It runs <em>only if the loop completes without hitting a `break` statement</em>.
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>Searching with For-Else</span>
                    </div>
                    <CodeBlock language="python" code={`def find_user(users, target_id):
    for user in users:
        if user.id == target_id:
            print("Found user!")
            break  # Skips the else block
    else:
        # Runs ONLY if we never found the user
        print("User not found in database.")
        raise ValueError("User missing")

# Without 'else', you need a flag variable:
# found = False
# for ...
#     if ... found = True; break
# if not found: ...`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Advanced: Building Your Own Iterator</h2>
                <p>
                    We used `range()` and lists, but what if you want to make your <em>own</em> objects loopable?
                    You just need to implement the Iterator Protocol.
                </p>
                <p>
                    Let's build a `Countdown` class that works exactly like `range()`, but backwards.
                </p>

                <CodeBlock language="python" code={`class Countdown:
    def __init__(self, start):
        self.current = start

    def __iter__(self):
        # 1. Boilerplate: Return self
        return self

    def __next__(self):
        # 2. Logic: Return value or raise StopIteration
        if self.current <= 0:
            raise StopIteration
        
        value = self.current
        self.current -= 1
        return value

# Usage
# It works in a for loop automatically!
for num in Countdown(3):
    print(num)  # 3, 2, 1`} />

                <ProTip><div className="info-icon"></div>
                    <div className="info-text">
                        <strong>Deep Dive:</strong> When you write <code>for x in obj</code>, Python calls <code>iter(obj)</code>.
                        If that returns an object with <code>__next__</code>, it calls <code>next()</code> repeatedly until
                        <code>StopIteration</code> is raised. That matches exactly what we built above.
                    </div></ProTip>
            </div>

            <div className="content-section">
                <h2>Loops vs List Comprehensions</h2>
                <p>
                    You cannot talk about loops in Python without mentioning List Comprehensions.
                    They are a concise way to create lists using a single line of looping logic.
                </p>
                <CodeBlock language="python" code={`numbers = [1, 2, 3, 4, 5]

# 1. The Standard Loop Way
squares = []
for n in numbers:
    squares.append(n * n)

# 2. The Comprehension Way
# [expression for item in iterable]
squares = [n * n for n in numbers]`} />

                <h3>When to Use Which?</h3>
                <div className="comparison-table">
                    <table>
                        <thead>
                            <tr>
                                <th>Feature</th>
                                <th>For Loop</th>
                                <th>Comprehension</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td><strong>Readability</strong></td>
                                <td>Better for complex logic/multiple steps.</td>
                                <td>Better for simple mapping/filtering.</td>
                            </tr>
                            <tr>
                                <td><strong>Performance</strong></td>
                                <td>Slightly slower (function call overhead of .append).</td>
                                <td>Faster (C-level optimization).</td>
                            </tr>
                            <tr>
                                <td><strong>Side Effects</strong></td>
                                <td>Designed for side effects (print, save to DB).</td>
                                <td><strong>Avoid!</strong> Don't use for side effects.</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <h3>Filtering with Comprehensions</h3>
                <p>
                    Comprehensions can also filter items using an `if` clause at the end.
                </p>
                <CodeBlock language="python" code={`# Get only even numbers
evens = [n for n in numbers if n % 2 == 0]

# Equivalent to:
# for n in numbers:
#     if n % 2 == 0:
#         evens.append(n)`} />
            </div>

            <div className="content-section">
                <h2>Advanced Looping with `itertools`</h2>
                <p>
                    Python's standard library includes a module called <code>itertools</code> that is dedicated to efficient looping.
                    It provides C-optimized iterators for common complex patterns.
                </p>

                <h3>1. itertools.product: Flattening Nested Loops</h3>
                <p>
                    Remember the nested loop example? You can rewrite it cleanly using `product`.
                    It generates the Cartesian product of input iterables.
                </p>
                <CodeBlock language="python" code={`import itertools

ranks = ['2', '3', '4', '5', '6', '7', '8', '9', '10', 'J', 'Q', 'K', 'A']
suits = ['Hearts', 'Diamonds', 'Clubs', 'Spades']

#  Nested Loop Way
deck = []
for suit in suits:
    for rank in ranks:
        deck.append((rank, suit))

# The itertools Way
# cleaner, flat, and expresses intent better
deck = list(itertools.product(ranks, suits))`} />

                <h3>2. itertools.cycle: Infinite Repetition</h3>
                <p>
                    Need to loop over a list forever? Don't use `while True` with a modulo operator.
                </p>
                <CodeBlock language="python" code={`colors = ["Red", "Green", "Yellow"]
light = itertools.cycle(colors)

next(light) # Red
next(light) # Green
next(light) # Yellow
next(light) # Red (Starts over!)`} />

                <h3>3. itertools.chain: Looping Over Multiple Lists</h3>
                <p>
                    If you need to loop over list A, then list B, then list C, don't concatenate them (`A + B + C`)!
                    That creates a massive new list in memory. Use `chain` to loop over them one by one without copying.
                </p>
                <CodeBlock language="python" code={`list1 = range(1000)
list2 = range(1000)

#  Bad (Creates 3rd list of 2000 items)
for i in list1 + list2: ...

# Good (Zero memory overhead)
for i in itertools.chain(list1, list2): ...`} />
            </div>

            <div className="content-section">
                <h2>Memory Mastery: Generators & Laziness</h2>
                <p>
                    Everything we've discussed so far assumes we have the data ready. But what if the data is massive?
                    What if it's infinite? Enter <strong>Generators</strong>.
                </p>
                <p>
                    A generator is a function that <em>pauses</em> its execution. Instead of `return`, it uses `yield`.
                    When you loop over a generator, it calculates one value, gives it to you, and waits for you to ask for the next one.
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>The Infinite Loop Generator</span>
                    </div>
                    <CodeBlock language="python" code={`# 1. A Generator Function
def fibonacci():
    a, b = 0, 1
    while True:
        yield a  # Pauses here and gives 'a' to the loop
        a, b = b, a + b

# 2. Consuming it
# This object takes constant memory, even if we loop forever
fib_gen = fibonacci()

for i in range(10):
    print(next(fib_gen)) # Manually getting next items`} />
                </div>

                <h3>Generator Expressions</h3>
                <p>
                    Just like List Comprehensions, but with parentheses <code>()</code>. They create a generator, not a list.
                </p>
                <CodeBlock language="python" code={`# List Comprehension (Eager)
# Creates a 100MB list in RAM instantly
squares_list = [x**2 for x in range(10000000)]

# Generator Expression (Lazy)
# Creates a generator object (48 bytes)
squares_gen = (x**2 for x in range(10000000))

# We can loop over it just the same!
for s in squares_gen:
    # process s...
    if s &gt; 100: break`} />

                <ProTip><div className="info-icon"></div>
                    <div className="info-text">
                        <strong>Rule of Thumb:</strong> If you are only going to loop over the result <strong>once</strong>, use a Generator.
                        It saves memory and starts processing instantly. If you need to access items by index or loop multiple times, use a List.
                    </div></ProTip>
            </div>

            <div className="content-section">
                <h2>Looping over Files & Streams</h2>
                <p>
                    A common task is reading a file line-by-line. Python makes this incredibly efficient because file objects are <strong>Iterators</strong>.
                </p>

                <h3>Lazy vs Eager Reading</h3>
                <p>
                    If you have a 10GB log file, you cannot load it all into memory.
                </p>
                <CodeBlock language="python" code={`#  Bad (Eager Pattern)
# Reads the ENTIRE file into a list of strings
with open("massive_log.txt") as f:
    lines = f.readlines() #  Memory Error!
    for line in lines:
        process(line)

# Good (Lazy Pattern)
# The file object yields one line at a time
with open("massive_log.txt") as f:
    for line in f: # Reads line, processes, discards from memory
        process(line)`} />
            </div>

            <div className="content-section">
                <h2>Looping Like a Pro: Built-in Helpers</h2>
                <p>
                    Python provides optimized built-in functions to alter how you loop without manually slicing lists or writing complex logic.
                </p>

                <h3>1. sorted() vs .sort()</h3>
                <p>
                    Use `sorted()` to iterate in order without changing the original list.
                </p>
                <CodeBlock language="python" code={`scores = [88, 50, 99, 70]

# Loop in ascending order
for score in sorted(scores):
    print(score) # 50, 70, 88, 99

# Loop in descending order
for score in sorted(scores, reverse=True):
    print(score)

#  Note: sorted() creates a NEW list. It uses O(n) memory.`} />

                <h3>2. reversed()</h3>
                <p>
                    To loop backwards, don't use slice notation `[::-1]` which creates a copy of the list (wasting memory).
                    Use `reversed()`, which returns an iterator (O(1) memory).
                </p>
                <CodeBlock language="python" code={`#  Slice (Uses Extra Memory)
for item in large_list[::-1]: ...

# Iterator (Zero Memory Overhead)
for item in reversed(large_list): ...`} />

                <h3>3. filter()</h3>
                <p>
                    Similar to `map()`, `filter()` creates an iterator that yields only items matching a condition.
                </p>
                <CodeBlock language="python" code={`# Get only valid users
users = [user1, None, user2, None]

#  Manual Check
for u in users:
    if u is not None:
        process(u)

# Filter Iterator
for u in filter(None, users):
    process(u)`} />
            </div>

            <div className="content-section">
                <h2>Strategies for Safe Modification</h2>
                <p>
                    One of the most common bugs in beginner Python code is modifying a list while iterating over it.
                    When you remove an item, all subsequent items shift left. The loop iterator, however, just moves the index forward.
                    This causes it to skip the item that just slid into the current spot.
                </p>

                <h3>Strategy 1: Iterate Over a Copy</h3>
                <p>
                    The simplest fix is to slice the list <code>[:]</code>. This creates a shallow copy.
                    The loop reads the original snapshot, but you modify the live list.
                </p>
                <CodeBlock language="python" code={`users = ["Active", "Banned", "Active", "Banned"]

# This works perfectly
for user in users[:]:
    if user == "Banned":
        users.remove(user)

print(users) # ["Active", "Active"]`} />

                <h3>Strategy 2: Collection Logic (The Better Way)</h3>
                <p>
                    Often, "removing items" is better thought of as "keeping valid items".
                    Use a list comprehension to create a <em>new</em> list of only what you want.
                </p>
                <CodeBlock language="python" code={`users = ["Active", "Banned", "Active", "Banned"]

# Create a new list instead of fighting the old one
users = [u for u in users if u != "Banned"]`} />
            </div>

            <div className="content-section">
                <h2>Deep Dive: How Enumerate Works</h2>
                <p>
                    We used `enumerate()` earlier, but how does it actually work? It's not magic; it's a generator!
                    Let's re-implement it to see the simple beauty of `yield`.
                </p>
                <CodeBlock language="python" code={`def my_enumerate(iterable, start=0):
    count = start
    for item in iterable:
        yield count, item  # Pause and return the pair
        count += 1

# Testing our version
colors = ["Red", "Blue"]
for i, color in my_enumerate(colors, 1):
    print(f"{i}: {color}")
    
# Output:
# 1: Red
# 2: Blue`} />
            </div>

            <div className="content-section common-pitfalls">
                <h2>Common Pitfalls</h2>

                <div className="pitfall-card">
                    <h3> Modifying While Iterating (The Wrong Way)</h3>
                    <p><strong>Why it's wrong:</strong> See the section above! But just to remind you:</p>
                    <CodeBlock language="python" code={`#  Buggy Code
for n in nums:
    if n % 2 == 0:
        nums.remove(n) # SKIPS ITEMS!`} />
                </div>

                <div className="pitfall-card">
                    <h3> Exhausting Iterators</h3>
                    <p><strong>Why it's wrong:</strong> Iterators (like `zip` or `map` or file objects) are one-time use. Once consumed, they are empty.</p>
                    <CodeBlock language="python" code={`numbers = [1, 2, 3]
squares = map(lambda x: x**2, numbers)

print(list(squares)) # [1, 4, 9]
print(list(squares)) # [] - Empty! The iterator is exhausted.`} />
                </div>

                <div className="pitfall-card">
                    <h3> Shadowing Built-in Names</h3>
                    <p><strong>Why it's wrong:</strong> If you use `list`, `str`, or `id` as variable names, you break Python's built-in functions.</p>
                    <CodeBlock language="python" code={`#  Naming variable 'list'
list = [1, 2, 3]

# Later in the code...
my_copy = list(range(5)) # TypeError! 'list' is now a list, not a class.

# Use descriptive names
numbers = [1, 2, 3]`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Language Comparison: Python vs The World</h2>
                <div className="comparison-table">
                    <table>
                        <thead>
                            <tr>
                                <th>Feature</th>
                                <th>C / Java (Classic)</th>
                                <th>JavaScript (Modern)</th>
                                <th>Python (Idiomatic)</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td><strong>Loop Style</strong></td>
                                <td>Index-based (i++)</td>
                                <td><code>.forEach()</code> or <code>for...of</code></td>
                                <td>Iterator-based (<code>in</code>)</td>
                            </tr>
                            <tr>
                                <td><strong>Simplicity</strong></td>
                                <td>Verbose</td>
                                <td>Verbose or callback-heavy</td>
                                <td>Clean & English-like</td>
                            </tr>
                            <tr>
                                <td><strong>Laziness</strong></td>
                                <td>No</td>
                                <td>No (array usually pre-allocated)</td>
                                <td>Yes (generators/range)</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <div className="content-section">
                <h2>Modern Python: Asynchronous Iteration</h2>
                <p>
                    In modern web and network programming, you often have to wait for data (e.g., from an API).
                    Blocking the entire loop while waiting is inefficient.
                </p>
                <p>
                    Python 3.5+ introduced <code>async for</code>. This allows the loop to "pause" and let other code run while waiting for the next item.
                </p>
                <CodeBlock language="python" code={`import asyncio

async def fetch_data():
    # Simulate network delay
    for i in range(3):
        await asyncio.sleep(1) # Pause here
        yield f"Data chunk {i}"

async def main():
    #  The loop yields control while waiting
    async for chunk in fetch_data():
        print(chunk)

# Run the async loop
# asyncio.run(main())`} />
            </div>

            <div className="content-section">
                <h2>Real World Application: processing A CSV</h2>
                <p>
                    Let's combine everything we've learned (Files, Splitting, Enumerate, List Comprehensions) into one real-world task:
                    Parsing a messy CSV file.
                </p>
                <CodeBlock language="python" code={`# raw_data.csv content:
# id,name,score
# 1,Alice,85
# 2,Bob,90
# 3,Charlie,invalid
# 4,Dave,75

cleaned_data = []

with open("raw_data.csv") as f:
    # 1. Enumerate to track line numbers for error logs
    for line_num, line in enumerate(f, start=1):
        
        # 2. Guard Clause: Skip header or empty lines
        line = line.strip()
        if not line or line.startswith("id"):
            continue

        # 3. Unpacking
        parts = line.split(",")
        if len(parts) != 3:
            print(f"Error on line {line_num}: Bad format")
            continue
            
        user_id, name, score_str = parts

        # 4. Data Processing
        if not score_str.isdigit():
            print(f"Warning line {line_num}: Invalid score for {name}")
            continue
            
        # 5. Success! Add to valid list
        cleaned_data.append({
            "id": int(user_id),
            "name": name,
            "score": int(score_str)
        })

print(f"Successfully loaded {len(cleaned_data)} users.")`} />
            </div>

            <div className="content-section">
                <h2>Summary: Best Practices Checklist</h2>
                <ProTip><div className="info-text">
                        <ul>
                            <li><strong>Prefer `for` loops</strong> over `while` loops whenever possible (safer, cleaner).</li>
                            <li><strong>Use `enumerate()`</strong> instead of `range(len())` if you need the index.</li>
                            <li><strong>Use List Comprehensions</strong> for simple mapping/filtering tasks (faster than `.append()`).</li>
                            <li><strong>Use Generators</strong> (`yield`) when processing large files or infinite data streams.</li>
                            <li><strong>Use `itertools`</strong> for complex iteration patterns (nested loops, chaining).</li>
                            <li><strong>Never modify</strong> the list you are currently looping over; iterate over a copy or use a comprehension instead.</li>
                        </ul>
                    </div></ProTip>
            </div>

        </div >
    );
};

export default PythonForLoops;

